import { ReactNode, useCallback } from 'react';

import { signOut } from 'aws-amplify/auth';
import { useAtom } from 'jotai';
import { HiArrowLeft, HiHome } from 'react-icons/hi2';

import { useSession } from './SessionContext';

import Button from '@/components/Button/Button';
import NotificationBanner from '@/components/NotificationBanner/NotificationBanner';
import SideBarItem from '@/components/SideBarItem/SideBarItem';
import { NotificationAtom, UserAtom } from '@/jotai/jotai';
import { PageItem } from '@/types/page';

type Props = {
  children: ReactNode;
};

const pageItems: PageItem[] = [
  {
    title: 'ダッシュボード',
    path: '/admin/dashboard',
    icon: <HiHome />,
    hasChildren: false,
  },
  {
    title: '利用実績管理',
    path: '/admin/achievements',
    icon: <HiHome />,
    hasChildren: true,
    children: [
      {
        title: '利用者一覧',
        path: '/admin/achievements/users',
      },
      {
        title: '全利用者',
        path: '/admin/achievements/all',
      },
      {
        title: '来所予約一覧',
        path: '/admin/reservations/visit',
      },
      {
        title: '送迎予約一覧',
        path: '/admin/reservations/transport',
      },
      {
        title: '夕食予約一覧',
        path: '/admin/reservations/dinner',
      },
      {
        title: '月間カレンダー登録',
        path: '/admin/reservations/calendar',
      },
    ],
  },
  {
    title: '実績管理（集計）',
    path: '/admin/achievements',
    icon: <HiHome />,
    hasChildren: true,
  },
  {
    title: '支援計画・モニタリング',
    path: '/admin/support-plans',
    icon: <HiHome />,
    hasChildren: false,
  },
  {
    title: '各種会議管理',
    path: '/admin/meetings',
    icon: <HiHome />,
    hasChildren: true,
  },
  {
    title: '請求管理',
    path: '/admin/billing',
    icon: <HiHome />,
    hasChildren: true,
  },
  {
    title: 'CRM',
    path: '/admin/crm',
    icon: <HiHome />,
    hasChildren: true,
  },
  {
    title: '利用者情報管理',
    path: '/admin/children',
    icon: <HiHome />,
    hasChildren: false,
  },

  {
    title: 'スタッフ勤怠管理',
    path: '/admin/staffs',
    icon: <HiHome />,
    hasChildren: true,
  },

  {
    title: 'アカウント管理',
    path: '/admin/accounts',
    icon: <HiHome />,
    hasChildren: false,
  },
  {
    title: 'マスタ管理',
    path: '/admin/master',
    icon: <HiHome />,
    hasChildren: true,
    children: [
      {
        title: '事業所マスタ',
        path: '/admin/offices',
      },
      {
        title: '車両マスタ',
        path: '/admin/vehicles',
      },
      {
        title: '学校マスタ',
        path: '/admin/schools',
      },
      {
        title: '定型文マスタ',
        path: '/admin/templates',
      },
    ],
  },
].concat(
  import.meta.env.VITE_ENV === 'dev'
    ? [
        {
          title: 'Lineアカウント管理（develop用）',
          path: '/admin/line-accounts',
          icon: <HiHome />,
          hasChildren: false,
        },
      ]
    : []
);

const DashboardLayout = ({ children }: Props) => {
  const { loadSession } = useSession();
  const [_, setNotification] = useAtom(NotificationAtom);
  const [user] = useAtom(UserAtom);

  const signout = useCallback(async () => {
    setNotification({
      headingLevel: 'h2',
      type: 'success',
      bannerStyle: 'standard',
      title: 'サインアウト',
      message: 'サインアウトしました。',
    });
    await signOut();
    loadSession({ forceRefresh: true });
  }, []);

  return (
    <div className="relative min-h-[100vh]">
      {/* ヘッダー部分 */}
      <div className="border-b-1 flex w-full flex-row items-center justify-between border-2 px-10 py-5">
        <img
          src="/icon.png"
          alt="logo"
          className="w-14"
        />
        <div className="flex flex-row items-center justify-center">
          <h2 className="mr-8 text-base font-700 text-black">
            {user ? `${user?.last_name}${user?.first_name}` : ''}
          </h2>
          <Button
            size="lg"
            variant="text"
            type="submit"
            onClick={signout}
          >
            ログアウト
          </Button>
        </div>
      </div>

      <div className="flex w-full">
        <div className="shadow-blue-gray-900/5 relative flex h-[calc(100vh-2rem)] w-full max-w-[20rem] flex-col bg-[#F2F2F24D] bg-clip-border p-4 text-gray-700">
          <div className="mb-2 flex justify-end p-4">
            <div className="text-xl">
              <HiArrowLeft />
            </div>
          </div>
          {pageItems.map((pageItem) => (
            <SideBarItem
              key={pageItem.title}
              item={pageItem}
            />
          ))}
        </div>
        <div className="relative flex w-full max-w-[calc(100%-20rem)] flex-col p-10">
          <NotificationBanner className="mx-auto mb-8 w-full" />
          {children}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
